import { useEffect, useState } from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { Button, TextArea, useToast } from '@morressier/alembic';
import { IDecisionModalProps } from './decisionModalHelper';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import ReviewSummaryAccordion from './ReviewSummaries';
import NotifyReviewersToggle from './NotifyReviewersToggle';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import {
  emailContentSchema,
  prepareResponseSchema
} from '@publishing-engine/domain/validation';
import { zodResolver } from '@hookform/resolvers/zod';
import { useProvisionalAcceptance } from 'src/api/commands';
import { createEditorialModal } from './EditorialDecisionModalWrapper';
import { EditorialNote, useEditorialDecisionNote } from './EditorialNote';
import { useSteps } from './hooks';
import StepHeader from '../../../pages/Organization/public/Journal/SubmissionUpload/components/StepHeader';
import { Overlay } from '../../Overlay';
import ComposeDecisionEmail, { EmailContentForm } from './ComposeDecisionEmail';
import { buildEmailPayload } from '../../../utils/normalizeEmailRecipients';
import AdjudicatorReviewSummary from './AdjudicatorReviewSummary';
import { useCommandEmailTemplates } from 'src/hooks/useCommandEmailTemplates';
import { useAttachmentSync } from '../../../hooks/useAttachmentSync';
import DecisionLayout from './DecisionLayout';
import ResponseToAuthorField from './ResponseToAuthorField';
import { useCOCContextData } from 'src/contexts/COCDataProvider';
import ModerateCOCComment from './ModerateCOCComment';
import RecommendationRounds from './RecommendationRounds';
import { useQueryClient } from '@tanstack/react-query';

export const ProvisionalAcceptanceModal = createEditorialModal(
  ({ onClose, submissionId, submissionContextId }: IDecisionModalProps) => {
    const toast = useToast();
    const queryClient = useQueryClient();
    const { hasCustomizableTemplates, templates } = useCommandEmailTemplates({
      organizationId: useSubmissionData().organizationId,
      commandName: 'ApproveSubmissionForPreProduction'
    });

    const { recommedationSummary } = useCOCContextData();
    const [notifyReviewers, setNotifyReviewers] = useState(false);
    const [notifyReviewerIds, setNotifyReviewerIds] = useState<string[]>([]);
    const { currentStep, goToNextStep, goToPreviousStep } = useSteps(
      hasCustomizableTemplates ? 2 : 0
    );

    const {
      reviewsSummary,
      refetchSubmission,
      organizationId,
      adjudicatorReviewsSummary,
      workflowResponse: { manuscriptId }
    } = useSubmissionData();

    const { editorNote, setEditorNote, submitEditorNote } =
      useEditorialDecisionNote({
        submissionId,
        submissionContextId,
        organizationId
      });

    const methods = useForm({
      defaultValues: {
        editorComment: ''
      },
      resolver: zodResolver(prepareResponseSchema)
    });
    useEffect(() => {
      queryClient.invalidateQueries({
        queryKey: ['reviews-summary', submissionId]
      });
      queryClient.invalidateQueries({
        queryKey: ['recommendation-summary', submissionId]
      });
    }, []);
    const {
      handleSubmit,
      reset,
      watch,
      formState: { errors },
      register
    } = methods;
    const emailContentMethods = useForm<EmailContentForm>({
      defaultValues: {
        emailContent: {
          body: '',
          subject: '',
          recipients: { to: [], cc: [], bcc: [] },
          attachments: []
        }
      },
      resolver: zodResolver(emailContentSchema)
    });
    const emailContent = emailContentMethods.watch('emailContent');
    const { setValue } = emailContentMethods;
    useEffect(() => {
      const defaultIndex = templates.findIndex(item => item.default);
      const selectedIndex = defaultIndex === -1 ? 0 : defaultIndex;
      const template = templates?.[selectedIndex || 0];

      if (!template) return;

      setValue(
        'emailContent.recipients.to',
        template.recipients.to
          ?.filter(email => email.trim() !== '')
          .map(email => ({ text: email })) ?? []
      );

      setValue(
        'emailContent.recipients.cc',
        template.recipients.cc
          ?.filter(email => email.trim() !== '')
          .map(email => ({ text: email })) ?? []
      );
      setValue(
        'emailContent.recipients.bcc',
        template.recipients.bcc
          ?.filter(email => email.trim() !== '')
          .map(email => ({ text: email })) ?? []
      );
      setValue('emailContent.subject', template.subject);
      setValue('emailContent.body', template.body);
    }, [templates.length]);

    // Auto-sync attachments from first step to email step
    useAttachmentSync({
      sourceForm: methods,
      targetForm: emailContentMethods,
      sourceField: 'attachments',
      targetField: 'emailContent.attachments'
    });

    const provisionalAcceptanceMutation = useProvisionalAcceptance(
      submissionId,
      submissionContextId,
      refetchSubmission,
      organizationId,
      buildEmailPayload(emailContent),
      notifyReviewerIds
    );
    const provisionallyAccept = async (formValues: any) => {
      const latestRecommendationCycle =
        recommedationSummary?.recommendationRounds?.at(-1)?.cycle?.at(-1);
      const COCcomments = latestRecommendationCycle?.participants
        .map(
          item =>
            (item?.recommendation?.moderatedCommentForAuthor ?? '') ||
            (item?.recommendation?.commentForAuthor ?? '')
        )
        .filter(Boolean)
        .join(' \n');
      const editorComment = COCcomments
        ? `${formValues?.editorComment ?? ''}\n${COCcomments}`
        : formValues?.editorComment ?? '';

      const res = await provisionalAcceptanceMutation.mutateAsync(
        editorComment
      );

      if (!res.isSuccess) {
        toast({
          variant: 'error',
          title: 'Error accepting for Provisional Acceptance',
          description:
            'There was an error while accepting submission for Provisional Acceptance.'
        });
        return;
      }

      await submitEditorNote(editorNote);
      localStorage.setItem(
        `${submissionId}.revisionRequest.editorComment`,
        editorComment
      );
      toast({
        variant: 'success',
        title: 'Submission In Pre-Production',
        description: 'The author has been notified via email of this decision.'
      });
      onClose();
    };

    useEffect(() => {
      const savedComment = localStorage.getItem(
        `${submissionId}.revisionRequest.editorComment`
      );
      if (savedComment) {
        reset({ editorComment: savedComment });
      }
    }, [reset]);

    const onSubmitFirstStep: SubmitHandler<any> = (_data, event) => {
      const nativeEvent = event?.nativeEvent as SubmitEvent | undefined;
      const submitter = nativeEvent?.submitter as HTMLButtonElement | null;
      if (!submitter) return;
      const action = submitter.value;
      if (hasCustomizableTemplates && action === 'EditMail') {
        goToNextStep();
      } else {
        methods.handleSubmit(provisionallyAccept)();
      }
    };

    const onSubmitEmailStep = () => {
      methods.handleSubmit(provisionallyAccept)();
    };
    const renderFirstStep = () => (
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmitFirstStep)}>
          <StepHeader
            currentStep={currentStep}
            totalSteps={hasCustomizableTemplates ? 2 : 0}
            heading="Proceed to Provisional Acceptance"
            description="Proceeding with this decision confirms you have reviewed the
                  submission against the provided Reviews. The author will be
                  informed of this decision along with the following
                  information:"
            marginY={2}
          />{' '}
          <Flex gap={6} flexDirection="column" overflow="auto">
            <Flex gap={2} flexDirection="column">
              <ResponseToAuthorField
                submissionId={submissionId}
                decisionType="ApproveSubmissionForPreProduction"
              />
              <Flex alignItems="center">
                <Text textStyle="headingItem">Response to Author</Text>
                <Box
                  as="span"
                  color="actionSecondaryCautionContent"
                  textStyle="headingItem"
                >
                  *
                </Box>
              </Flex>
              <TextArea
                value={watch('editorComment')}
                {...register('editorComment')}
                placeholder="Add feedback to the authors"
                isInvalid={Boolean(errors.editorComment)}
                errorMessage={errors?.editorComment?.message}
                rows={4}
                maxLength={10000}
              />
            </Flex>
            <EditorialNote value={editorNote} onChange={setEditorNote} />
            {recommedationSummary?.recommendationRounds && (
              <ModerateCOCComment
                RecommendationRound={recommedationSummary?.recommendationRounds}
              />
            )}
            <Flex direction="column" mb={3}>
              {Boolean(reviewsSummary?.length) && (
                <ReviewSummaryAccordion
                  reviews={reviewsSummary ?? []}
                  submissionId={submissionContextId}
                />
              )}
              <AdjudicatorReviewSummary
                adjudicatorReview={adjudicatorReviewsSummary ?? []}
              />
            </Flex>
          </Flex>
        </form>
      </FormProvider>
    );
    const renderEmailStep = () => (
      <FormProvider {...emailContentMethods}>
        <form onSubmit={emailContentMethods.handleSubmit(onSubmitEmailStep)}>
          <StepHeader
            currentStep={2}
            totalSteps={2}
            heading="Send Email Notification"
            description="Compose an email to notify the author about the provisional acceptance."
            marginY={2}
          />

          <ComposeDecisionEmail
            organizationId={organizationId}
            commandName="ApproveSubmissionForPreProduction"
            formPayload={methods.getValues()}
          />
          <div style={{ display: 'none' }}>
            <EditorialNote value={editorNote} onChange={setEditorNote} />
          </div>
        </form>
      </FormProvider>
    );

    const renderDecisionSidebar = () => <RecommendationRounds fullWidth />;

    const renderReviewerNotification = () => (
      <NotifyReviewersToggle
        submissionId={submissionId}
        organizationId={organizationId}
        commandName="ApproveSubmissionForPreProduction"
        isEnabled={notifyReviewers}
        selectedReviewerIds={notifyReviewerIds}
        onChange={(enabled, reviewerIds) => {
          setNotifyReviewers(enabled);
          setNotifyReviewerIds(reviewerIds);
        }}
      />
    );

    const renderFooter = () => {
      if (currentStep === 1) {
        return (
          <Flex
            paddingY={4}
            paddingX={6}
            bottom={0}
            zIndex={1}
            width="100%"
            justifyContent="space-between"
            position="sticky"
            bgColor="surfacePrimaryFill"
          >
            <Button variant="caution" onClick={onClose}>
              Exit
            </Button>
            <Flex gap={3}>
              {hasCustomizableTemplates && (
                <Button onClick={goToNextStep} variant="secondary">
                  {'Edit Mail & Submit Decision'}
                </Button>
              )}
              <Button
                onClick={() => methods.handleSubmit(provisionallyAccept)()}
                isLoading={
                  !hasCustomizableTemplates &&
                  provisionalAcceptanceMutation.isPending
                }
              >
                {'Submit Decision'}
              </Button>
            </Flex>
          </Flex>
        );
      }

      return (
        <Flex
          paddingY={4}
          paddingX={6}
          bottom={0}
          zIndex={1}
          width="100%"
          justifyContent="space-between"
          position="sticky"
          bgColor="surfacePrimaryFill"
        >
          <Button onClick={goToPreviousStep} variant="secondary">
            Back
          </Button>
          <Button
            onClick={() =>
              emailContentMethods.handleSubmit(onSubmitEmailStep)()
            }
            isLoading={provisionalAcceptanceMutation.isPending}
          >
            Submit Decision
          </Button>
        </Flex>
      );
    };

    return (
      <Overlay backgroundColor="surfaceSecondaryFill">
        <DecisionLayout
          onClose={onClose}
          manuscriptId={manuscriptId}
          sidebar={currentStep === 1 ? renderDecisionSidebar() : undefined}
        >
          <Box marginTop={2}>
            {currentStep === 1
              ? renderFirstStep()
              : hasCustomizableTemplates
              ? renderEmailStep()
              : null}
            {renderReviewerNotification()}
          </Box>
        </DecisionLayout>
        {renderFooter()}
      </Overlay>
    );
  }
);
